// src/shared/RtcVoiceAndCursors.jsx
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ensureAuth, dbWrite, dbOn, dbRemove, dbPush } from '../firebase';

const clamp01 = v => Math.max(0, Math.min(1, v));

export default function RtcVoiceAndCursors({ roomId, userId, pdfWrapId = 'pdf-wrap' }) {
  const pcRef = useRef(null);
  const dcRef = useRef(null);
  const [remoteCursors, setRemoteCursors] = useState({});
  const [connected, setConnected] = useState(false);

  const authUidRef = useRef(null);
  const clientIdRef = useRef(`c_${Math.random().toString(36).slice(2, 8)}`);

  const pendingCandidatesRef = useRef([]); // buffer for early ICE
  const I_AM_CALLER = useRef(false);
  const OFFER_WRITTEN = useRef(false);
  const CLEANED = useRef(false);

  const getWrapEl = () => document.getElementById(pdfWrapId);

  useEffect(() => {
    let pc;
    let unOffer, unAnswer, unCand, unPeers, unOfferPresence, unAnswerPresence;
    let closed = false;

    (async () => {
      if (!roomId || !userId) return;

      // 1) Auth
      const u = await ensureAuth();
      authUidRef.current = u?.uid;
      const myUid = authUidRef.current;
      const myClient = clientIdRef.current;
      if (!myUid) {
        console.error('[RTC] No auth UID; cannot proceed.');
        return;
      }
      console.log('[RTC] Auth OK, uid =', myUid, 'client =', myClient);

      // 2) Create RTCPeerConnection with basic STUN
const pc = new RTCPeerConnection({
  iceServers: [
    { urls: 'stun:stun.l.google.com:19302' },
    { urls: 'stun:stun1.l.google.com:19302' },
    // Twilio STUN is fine too — but **no** query string:
    // { urls: 'stun:global.stun.twilio.com:3478' },

    // If/when you add TURN (for NATs), use this pattern:
    // { urls: 'turn:your.turn.server:3478', username: 'user', credential: 'pass' }
  ]
});
      pcRef.current = pc;
      pc.onconnectionstatechange = () => console.log('[RTC] state =', pc.connectionState);
      pc.onsignalingstatechange = () => console.log('[RTC] signaling =', pc.signalingState);
      pc.oniceconnectionstatechange = () => {
        if (pc.iceConnectionState === 'disconnected' || pc.iceConnectionState === 'failed') {
          setConnected(false);
        }
      };

      // 3) Audio (optional)
      try {
        const s = await navigator.mediaDevices.getUserMedia({
          audio: { echoCancellation: true, noiseSuppression: true },
          video: false
        });
        s.getTracks().forEach(t => pc.addTrack(t, s));
        console.log('[RTC] Mic granted; audio track added');
      } catch (err) {
        console.warn('[RTC] Mic not granted, continuing with cursors-only:', err?.name || err);
        try { pc.addTransceiver('audio', { direction: 'recvonly' }); } catch {}
      }
      const remoteAudio = new Audio(); remoteAudio.autoplay = true; remoteAudio.playsInline = true;
      pc.ontrack = e => { remoteAudio.srcObject = e.streams[0]; };

      // 4) DataChannel
      let dc = pc.createDataChannel('cursors', { ordered: false, maxRetransmits: 0 });
      const attachDC = (ch) => {
        dcRef.current = ch;
        ch.onopen = () => { setConnected(true); console.log('[RTC] DataChannel open'); };
        ch.onclose = () => { setConnected(false); console.log('[RTC] DataChannel closed'); };
        ch.onmessage = (e) => {
          try {
            const m = JSON.parse(e.data);
            setRemoteCursors(p => ({ ...p, [m.id]: { x: m.x, y: m.y } }));
          } catch {}
        };
      };
      attachDC(dc);
      pc.ondatachannel = (evt) => {
        if (evt.channel?.label === 'cursors') {
          attachDC(evt.channel);
        }
      };

      // 5) ICE: write ours; buffer incoming until remote SDP is applied
      pc.onicecandidate = e => {
        if (e.candidate) {
          dbPush(`webrtc-rooms/${roomId}/candidates/${myUid}/${myClient}`, e.candidate.toJSON())
            .catch(err => console.error('[RTC] dbPush candidate failed', err));
        }
      };

      const drainCandidatesIfAny = async () => {
        // apply any buffered ICE candidates after remoteDescription is set
        if (!pc.remoteDescription || !pc.remoteDescription.type) return;
        const arr = pendingCandidatesRef.current.splice(0);
        for (const cand of arr) {
          try { await pc.addIceCandidate(cand); }
          catch (e) { console.warn('[RTC] addIceCandidate (drain) failed', e?.message || e); }
        }
      };

      unCand = dbOn(`webrtc-rooms/${roomId}/candidates`, async (all) => {
  if (!all || pc.signalingState === 'closed') return;
        for (const [peerUid, clientMap] of Object.entries(all)) {
          if (!clientMap) continue;
          for (const [peerClient, list] of Object.entries(clientMap)) {
            if (peerUid === myUid && peerClient === myClient) continue; // skip our own
            for (const cand of Object.values(list)) {
              if (!pc.remoteDescription || !pc.remoteDescription.type) {
                pendingCandidatesRef.current.push(cand);
              } else {
                try { await pc.addIceCandidate(cand); }
                catch (e) { console.warn('[RTC] addIceCandidate failed', e?.message || e); }
              }
            }
          }
        }
      });

      // 6) Presence: write myself, read all, elect a single caller (smallest clientId)
      await dbWrite(`webrtc-rooms/${roomId}/peers/${myUid}/${myClient}`, true);

      const decideCaller = (peersObj) => {
        // peersObj: { uid: { clientId: true, ... }, ... }
        const allClients = [];
        for (const uid of Object.keys(peersObj || {})) {
          const cmap = peersObj[uid] || {};
          for (const cid of Object.keys(cmap)) allClients.push(cid);
        }
        allClients.sort(); // lexicographic
        const leader = allClients[0]; // smallest
        const iAmLeader = leader === myClient;
        I_AM_CALLER.current = iAmLeader;
        return iAmLeader;
      };

unPeers = dbOn(`webrtc-rooms/${roomId}/peers`, async (peers) => {
  if (closed || OFFER_WRITTEN.current) return;

  // Count total clients present
  let total = 0;
  for (const uid of Object.keys(peers || {})) {
    total += Object.keys(peers[uid] || {}).length;
  }
  if (total < 2) return; // ✅ don't elect/offer until both peers are present

  // Elect the lexicographically-smallest clientId as caller
  const allClients = [];
  for (const uid of Object.keys(peers || {})) {
    for (const cid of Object.keys(peers[uid] || {})) allClients.push(cid);
  }
  allClients.sort();
  const leader = allClients[0];
  const iAmCaller = leader === clientIdRef.current;
  I_AM_CALLER.current = iAmCaller;

  if (iAmCaller) {
    console.log('[RTC] Elected caller; preparing to create offer');
    const offer = await pc.createOffer({ offerToReceiveAudio: true });
    await pc.setLocalDescription(offer);
    await dbWrite(`webrtc-rooms/${roomId}/offer`, {
      fromUid: authUidRef.current,
      fromClient: clientIdRef.current,
      sdp: pc.localDescription.toJSON()
    });
    OFFER_WRITTEN.current = true;
  }
});


      // 7) Answer incoming offer (if I am not caller)
unOffer = dbOn(`webrtc-rooms/${roomId}/offer`, async (offer) => {
  if (!offer) return;
  if (offer.fromUid === authUidRef.current && offer.fromClient === clientIdRef.current) return;

  // ✅ if I’m the elected caller, I must NOT process a remote offer
  if (I_AM_CALLER.current) {
    console.debug('[RTC] Ignoring remote offer because I am the caller.');
    return;
  }

  if (pc.signalingState !== 'stable') return; // already set something
  console.log('[RTC] Got remote offer from', offer.fromUid, offer.fromClient, '; answering');
  await pc.setRemoteDescription(offer.sdp);
  await drainCandidatesIfAny();
  const ans = await pc.createAnswer();
  await pc.setLocalDescription(ans);
  await dbWrite(`webrtc-rooms/${roomId}/answer`, {
    fromUid: authUidRef.current,
    fromClient: clientIdRef.current,
    sdp: pc.localDescription.toJSON()
  });
});

      // 8) Apply remote answer (if I am caller)
unAnswer = dbOn(`webrtc-rooms/${roomId}/answer`, async (answer) => {
  if (!answer) return;
  if (answer.fromClient === clientIdRef.current && answer.fromUid === authUidRef.current) return;

  // ✅ Only set remote answer when we actually have a local offer outstanding
  if (pc.signalingState !== 'have-local-offer') {
    console.debug('[RTC] Ignoring remote answer; not in have-local-offer (state=', pc.signalingState, ')');
    return;
  }

  console.log('[RTC] Got remote answer; setting');
  try { await pc.setRemoteDescription(answer.sdp); }
  catch (e) { console.warn('[RTC] setRemoteDescription(answer) failed', e?.message || e); }
  await drainCandidatesIfAny();
});


      // 9) Presence logs (debug)
      unOfferPresence = dbOn(`webrtc-rooms/${roomId}/offer`, (val) => {
        console.log('[RTC] Offer presence:', !!val);
      });
      unAnswerPresence = dbOn(`webrtc-rooms/${roomId}/answer`, (val) => {
        console.log('[RTC] Answer presence:', !!val);
      });
    })().catch(err => console.error('[RTC] init failed', err));

    return () => {
      if (CLEANED.current) return;
      CLEANED.current = true;
      const myUid = authUidRef.current;
      const myClient = clientIdRef.current;
      closed = true;
      try { dcRef.current?.close(); } catch {}
      try { pcRef.current?.close(); } catch {}
      if (myUid) {
        dbRemove(`webrtc-rooms/${roomId}/candidates/${myUid}/${myClient}`).catch(() => {});
        dbRemove(`webrtc-rooms/${roomId}/peers/${myUid}/${myClient}`).catch(() => {});
        // do NOT nuke offer/answer/candidates globally; another peer may still be in the room
      }
      unOffer && unOffer();
      unAnswer && unAnswer();
      unCand && unCand();
      unPeers && unPeers();
      unOfferPresence && unOfferPresence();
      unAnswerPresence && unAnswerPresence();
      setConnected(false);
    };
  }, [roomId, userId, pdfWrapId]);

  // send local cursor (mousemove or your DigiLens reticle)
  const lastRef = useRef(0);
  useEffect(() => {
    const wrap = getWrapEl(); if (!wrap) return;
    const onMove = (e) => {
      const now = performance.now(); if (now - lastRef.current < 33) return; lastRef.current = now;
      const r = wrap.getBoundingClientRect();
      const x = clamp01((e.clientX - r.left) / r.width);
      const y = clamp01((e.clientY - r.top) / r.height);
      if (dcRef.current?.readyState === 'open') {
        dcRef.current.send(JSON.stringify({ id: userId, x, y }));
      }
    };
    wrap.addEventListener('mousemove', onMove);
    return () => wrap.removeEventListener('mousemove', onMove);
  }, [userId]);

  // render cursors *into* the pdf wrapper via portal
  const target = getWrapEl();
  if (!target) return null;

  return createPortal(
    <>
      {Object.entries(remoteCursors).map(([id, p]) => (
        <div
          key={id}
          title={id}
          style={{
            position: 'absolute', left: `${p.x * 100}%`, top: `${p.y * 100}%`,
            transform: 'translate(-50%,-50%)',
            width: 14, height: 14, borderRadius: '50%',
            background: id === userId ? '#3cff8f' : '#00e5ff',
            outline: '2px solid rgba(0,0,0,.35)', pointerEvents: 'none', zIndex: 9999
          }}
        />
      ))}
      {!connected && (
        <div style={{
          position: 'absolute', left: 12, bottom: 12, zIndex: 9999,
          background: 'rgba(0,0,0,.6)', color: '#fff', padding: '6px 10px',
          borderRadius: 6, fontSize: 12
        }}>
          Connecting voice & cursors…
        </div>
      )}
    </>,
    target
  );
}
